import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/apiClient';

// Loaded dynamically (not a static <script> in public/index.html) so the client-id can vary by
// environment -- see REACT_APP_PAYPAL_CLIENT_ID in .env.development / .env.production. A
// live-mode SDK session pointed at an order created server-side in sandbox mode (or vice versa)
// can't find that order and fails with a generic, unhelpful error from PayPal itself.
let paypalSdkPromise = null;

function loadPayPalSdk() {
    if (window.paypal) {
        return Promise.resolve();
    }
    if (paypalSdkPromise) {
        return paypalSdkPromise;
    }

    const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
    if (!clientId) {
        return Promise.reject(new Error("PayPal is not configured (missing client id)."));
    }

    paypalSdkPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=GBP`;
        script.onload = () => resolve();
        script.onerror = () => {
            paypalSdkPromise = null; // allow a later retry (e.g. a different component mount)
            reject(new Error("Could not load PayPal. Please check your connection and try again."));
        };
        document.body.appendChild(script);
    });
    return paypalSdkPromise;
}

// Test-only: the module-level cache above must not leak a pending/real-script promise from one
// test into the next -- there is no real network in jsdom to ever resolve or reject it there.
export function __resetPayPalSdkPromiseForTests() {
    paypalSdkPromise = null;
}

// #201: customer-facing wording for a declined capture lives here, not on the backend --
// the backend returns only a category (domain/paypal.py's CATEGORY_* constants), never
// PayPal's own issue code or a raw error message.
const CATEGORY_MESSAGES = {
    declined: "Your payment method was declined. Please try a different card or payment method, or contact your bank.",
    action_required: "Your bank needs you to complete an extra verification step. Please try again and follow any prompts from your bank or PayPal.",
    already_paid: "This booking has already been paid for.",
    // #203: assigned by the backend's own reconciliation check, not one of PayPal's decline
    // reasons -- the rare case where a payment is captured but the desk it was for has since
    // been booked, and paid for, by someone else while this one sat unconfirmed past its hold.
    booking_unavailable: "Your payment was taken, but this desk has since been booked by someone else. Please contact us at help@thetypingrooms.co.uk quoting your PayPal receipt and we will arrange a refund.",
    generic: "Sorry, we couldn't process your payment. Please try again, or contact us if the problem continues.",
};

const PayPalButton = ({
    amount,
    group_booking_id,
    setIsErrorModalOpen,
    setApiErrorMessage
}) => {

    const paypalRef = useRef();
    const navigate = useNavigate();

    // #204: a decline is shown inline, next to the still-mounted buttons, instead of in the
    // modal that resets the whole booking form on close. `retryable` is false only once a
    // decline is terminal (already_paid) -- there is nothing to retry, so the buttons hide.
    const [declineMessage, setDeclineMessage] = useState(null);
    const [retryable, setRetryable] = useState(true);

    const API_URL = process.env.REACT_APP_API_URL || "/api";

    useEffect(() => {
        let cancelled = false;
        let paypalButtons = null;

        function renderButtons() {
            const buttons = window.paypal.Buttons({
                createOrder: async () => {
                    // Clear any decline message left over from a previous attempt -- this is a retry.
                    setDeclineMessage(null);

                    // #200: the order is created server-side. No amount is sent here at all --
                    // the backend derives it from the stored booking, never from the client, so
                    // there is nothing here for a tampered request to override.
                    const response = await apiFetch(`${API_URL}/bookings/create-order`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ group_booking_id }),
                    });

                    const result = await response.json();

                    if (!response.ok) {
                        throw new Error(result.error || "Could not start payment. Please try again.");
                    }

                    return result.id; // the PayPal SDK expects createOrder to resolve to the order id
                },
                onApprove: async (data) => {
                    // #201: captured server-side. `actions.order.capture()` is no longer called
                    // here at all -- only `data.orderID`, the id createOrder returned, is sent on.
                    const response = await apiFetch(`${API_URL}/bookings/complete-order`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            group_booking_id: group_booking_id,
                            paypal_order_id: data.orderID,
                        }),
                    });

                    const result = await response.json();

                    if (!response.ok) {
                        // A genuine fault (PayPal unreachable, our own server error) -- not a
                        // decline, which arrives as a 200 with status "error" and is handled below.
                        throw new Error(result.error || "Could not process your payment. Please try again.");
                    }

                    if (result.status === "error") {
                        // A decline is a normal business outcome, not a fault -- #204 keeps the
                        // booking (dates, desks, price, group_booking_id) and the buttons intact so
                        // the customer can just try again. Only "already_paid" is terminal: there is
                        // no order left to retry against.
                        // "already_paid" and "booking_unavailable" are both terminal -- there is
                        // no order left to retry against in the first case, and no desk left to
                        // retry for in the second (#203).
                        setDeclineMessage(CATEGORY_MESSAGES[result.category] || CATEGORY_MESSAGES.generic);
                        setRetryable(!["already_paid", "booking_unavailable"].includes(result.category));
                        return;
                    }

                    navigate(`/booking-confirmation/${group_booking_id}`);
                },
                onCancel: () => {
                    // The buyer closed the PayPal popup without completing payment. Not an error --
                    // #204 requires this stays distinct from onError. Nothing to show or reset; the
                    // booking hold and buttons are untouched.
                },
                onError: (error) => {
                    // A genuine fault (PayPal SDK error, our own server unreachable) -- distinct from
                    // a decline, which arrives through onApprove above and never reaches here.
                    setApiErrorMessage(error.message);
                    setIsErrorModalOpen(true);
                }
            });

            buttons.render(paypalRef.current);
            return buttons;
        }

        loadPayPalSdk()
            .then(() => {
                // The component may have unmounted, or this effect re-run for a dependency
                // change, while the SDK script was still loading.
                if (cancelled || !paypalRef.current) return;
                paypalButtons = renderButtons();
            })
            .catch((error) => {
                if (cancelled) return;
                setApiErrorMessage(error.message);
                setIsErrorModalOpen(true);
            });

        return () => {
            cancelled = true;
            if (paypalButtons) {
                paypalButtons.close(); // Clean up on unmount
            }
        };
    }, [API_URL, group_booking_id, navigate, setApiErrorMessage, setIsErrorModalOpen]);

    return (
        <div>
            {declineMessage && (
                <p className="paypal-decline-message" role="alert">{declineMessage}</p>
            )}
            <div ref={paypalRef} data-testid="paypal-buttons" style={{ display: retryable ? 'block' : 'none' }} />
        </div>
    );
};

export default PayPalButton;
